import React, { ReactElement, ReactNode } from 'react';
import generateLocator from 'utils/generate-locator';
import { FieldState } from './form-state';
import { validateFormField } from './validators';

interface FormLabelWrapperProps {
  name: string,
  htmlFor?: string,
  id?: string,
  className?: string
  input: ReactElement,
  requiredLabel?: boolean
}

export const FormLabelWrapper = (props: FormLabelWrapperProps) => {
  return <div id={props.id} className='flex flex-row w-full p-2 text-left'>
    <div className='w-40 place-self-center'>
      <label className='align-middle flex flex-row' htmlFor={props.htmlFor || props.input.props.id}>
        <span className='flex flex-auto'>{props.name}</span>
        {props.requiredLabel && <span className='flex flex-initial pr-4 text-lg'><strong>&#42;</strong></span>}
      </label>
    </div>
    {props.input}
  </div>;
};

type FormInputProps =
  React.InputHTMLAttributes<HTMLInputElement>
  & React.InputHTMLAttributes<HTMLTextAreaElement>
  & {
    id?: string,
    textarea?: boolean,
    error?: string
    className?: string,
    validClassName?: string,
    invalidClassName?: string,
    type?: string,
  };

export const FormInput = (props: FormInputProps) => {
  let classes = '';
  let validClasses = '';
  let invalidClasses = '';
  if (!props.className && !props.invalidClassName && !props.validClassName) {
    classes = 'w-full text-base font-semibold rounded-md shadow-md p-2 border-2 hover:border-gray-400 hover:border-black';
    validClasses = 'border-gray-300';
    invalidClasses = 'border-red-200';
  } else {
    classes = props.className || '';
    validClasses = props.validClassName || '';
    invalidClasses = props.invalidClassName || '';
  }

  const fieldProps = {
    ...props,
    className: `${classes} ${props.error ? invalidClasses : validClasses}`,
  };
  delete fieldProps.textarea;

  let inputElement;
  if (props.textarea) {
    inputElement = <textarea {...fieldProps} />;
  } else {
    inputElement = <input {...fieldProps} />;
  }

  return <div className='w-full'>
    <div>{inputElement}</div>
    {props.error && <FormErrorMessage id={props.id && `${props.id}-err`}>{props.error}</FormErrorMessage>}
  </div>;
};

export const FormErrorMessage = ({ id, children }: { id?: string, children: ReactNode }) => {
  return <span id={id} className='text-xs text-red-600'>{children}</span>;
};

interface FormFieldProps {
  name?: string,
  id?: string,
  field: FieldState,
  validateOnChange?: boolean,
  textarea?: boolean,
  type?: string,
  hideRequiredStar?: boolean,
}

export const FormField = (props: FormFieldProps) => {
  const name = props.name || props.field.visibleName;
  const id = props.id || generateLocator(name);
  return <FormLabelWrapper
    name={name}
    requiredLabel={!props.hideRequiredStar && props.field.required}
    input={<FormInput
      textarea={props.textarea}
      type={props.type}
      id={id}
      value={props.field.value || ''}
      error={props.field.error || ''}
      onChange={event => {
        props.field.setValue && props.field.setValue(event.target.value);
        props.validateOnChange && validateFormField(props.field, event.target.value);
      }}
    />}
  />;
};
